import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { fetchProviders, FetchProvidersParams, fetchToken, Provider, ProviderItem } from '../../../api/client';
import { AxiosError } from 'axios';

export interface GeneralState {
  query: QueryApiData;
  token: TokenApiData;
}

export type ApiStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface ApiData {
  status: ApiStatus;
  error: string;
}

export interface TokenApiData extends ApiData {
  token: null | string;
}

export interface QueryApiData extends ApiData {
  queryString: string;
  state: null | string;
  count: number;
  page: number;
  totalPages: number;
  providers: ProviderItem[];
  requestTime: number;
}

export const initialState: GeneralState = {
  query: {
    queryString: '',
    status: 'idle',
    error: '',
    state: null,
    count: 0,
    page: 1,
    totalPages: 1,
    providers: [],
    requestTime: 0,
  },
  token: {
    status: 'idle',
    error: '',
    token:
      'eyJraWQiOiJ5RzdmM3VlZ1RVcmJzdm5sSG5UWkRIMEVFZlVqeTBGT1dic2RiN2dDNEhRIiwiYWxnIjoiUlMyNTYifQ.eyJ2ZXIiOjEsImp0aSI6IkFULnlMdlZvVXlkWmRUeTNmMGtFWTQ3M2JfdGZ1NW9Ic1JkSzd5X3RZWWtzc0EiLCJpc3MiOiJodHRwczovL21ldHJvcGx1c3BvcnRhbC5va3RhLmNvbS9vYXV0aDIvYXVzYTNjYTR1b1R5ZTVxQm0yOTciLCJhdWQiOiJhcGk6Ly9tdWxlc29mdCIsImlhdCI6MTcyNTMwMDY5NSwiZXhwIjoxNzI1MzA0Mjk1LCJjaWQiOiIwb2FzY2U4eXJkNGNlQ2dzSDI5NyIsInNjcCI6WyJjbGllbnRfY3JlZGVudGlhbHNfc2NvcGUiXSwic3ViIjoiMG9hc2NlOHlyZDRjZUNnc0gyOTcifQ.B1crS1ZBvSyP8-85bQemV7IhtaTCSySg7Hi7-i10n9sdd7mm8zTI-_Ji0SIw8reBn92uZ8URaGceaSZ9PsjB4tD7c5x5C7Y7W4evjkpRRMM_MW1DH1HAiKh-9OCS-RUDax0JQdvJCwkABxMBofe85L_mGp8-Kn-Gs-jNAGWjw62eBUxAyM--CHAcXH1V_dTtFhhlRjsg0GnH_uVPQZIv1pCxmAMdYmM2REs5ptEphpiNYnKKp_KWl3y1ocRBGTF09BBp7G1ZxEDEV38tKWZmcsE-HfRcp1aRrOWfj8b6U873HNEK6z1bBYNP0AuoGmVUmIclJoQbK2nrOmU8pDJO-g',
  },
};

export const generalSlice = createSlice({
  name: 'general',
  initialState,
  reducers: {
    // Set main query
    setQuery(state: GeneralState, action: PayloadAction<string>) {
      const query = action.payload as string;

      return {
        ...state,
        query: {
          ...state.query,
          queryString: query,
        },
      };
    },
    // Update page
    setPage(state: GeneralState, action: PayloadAction<number>) {
      const page = action.payload as number;

      return {
        ...state,
        query: {
          ...state.query,
          page: page,
        },
      };
    },
  },
  extraReducers(builder) {
    builder
      // 1. Token
      .addCase(getToken.pending, (state: GeneralState) => {
        state.token.error = '';
        state.token.token = null;
        state.token.status = 'loading';
      })
      .addCase(getToken.fulfilled, (state: GeneralState, action) => {
        const serverResponse = action.payload;

        state.token.error = '';
        state.token.status = 'succeeded';
        state.token.token = serverResponse;
      })
      .addCase(getToken.rejected, (state: GeneralState, action) => {
        state.token.error = action.payload as string;
        state.token.status = 'failed';
        state.token.token = null;
      })

      // 2. Main query
      .addCase(getProviders.pending, (state: GeneralState) => {
        state.query.error = '';
        state.query.status = 'loading';
        state.query.count = 0;
        state.query.totalPages = 1;
        state.query.providers = [];
      })
      .addCase(getProviders.fulfilled, (state: GeneralState, action) => {
        const serverResponse = action.payload;
        const { data: responseData, time } = serverResponse;
        const { Results: providers, Count: count, state: queryState, page } = responseData;

        return {
          ...state,
          query: {
            ...state.query,
            error: '',
            state: queryState,
            status: 'succeeded',
            count: count,
            page: page,
            totalPages: Math.floor(count / 20),
            providers: providers,
            requestTime: time,
          },
        };

        // state.query.error = '';
        // state.query.state = queryState;
        // state.query.status = 'succeeded';
        // state.query.count = count;
        // state.query.page = page;
        // state.query.totalPages = Math.floor(count / 20);
        // state.query.providers = providers;
      })
      .addCase(getProviders.rejected, (state: GeneralState, action) => {
        state.token.error = action.payload as string;

        state.query.error = '';
        state.query.state = null;
        state.query.status = 'failed';
        state.query.count = 0;
        state.query.page = 1;
        state.query.totalPages = 1;
        state.query.providers = [];
        state.query.requestTime = 0;
      });
  },
});

export const getGeneralSlice = (state: RootState) => state.general;

// 1. Token query
export const getToken = createAsyncThunk('general/getToken', async (_, { getState, rejectWithValue }) => {
  try {
    const response = await fetchToken();
    const { access_token: token } = response;

    return token;
  } catch (error) {
    const err = error as AxiosError;
    return rejectWithValue(err.message);
  }
});

// 2. Main query
export const getProviders = createAsyncThunk(
  'general/getProviders',
  async (byState: boolean = false, { getState, rejectWithValue }) => {
    try {
      const currentState = getState() as RootState;

      const { token: tokenData, query: queryData } = currentState.general;
      const { token } = tokenData;
      const { queryString, state, page } = queryData;

      let params: FetchProvidersParams = {
        //token: token ?? '',
      };
      if (byState) {
        params = {
          ...params,
          state: state ?? '',
          page,
        };
      } else {
        params = {
          ...params,
          query: queryString,
        };
      }

      const response = await fetchProviders(params);

      const { data, success, errorMessage } = response;
      if (!success) {
        return rejectWithValue(errorMessage);
      }

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return rejectWithValue(err.message);
    }
  },
);

export const generalActions = generalSlice.actions;
// @ts-ignore
export const generalReducer = generalSlice.reducer;
